"use client";

import { useEffect } from "react";
import { useLocale } from "next-intl";
import { MESSENGERS } from "@/lib/schemas";

/**
 * WebMCP (navigator.modelContext, W3C Community Draft): регистрирует форму заявки как
 * browser-native инструмент для ИИ-агента, который открыл страницу в браузере.
 *
 * Полифилла нет намеренно: тащить сотни килобайт всем посетителям ради возможности
 * агента - плохая сделка. Нет поддержки в браузере - тихий no-op.
 */

type ToolResult = { content: Array<{ type: "text"; text: string }> };

type ModelContext = {
  registerTool?: (tool: {
    name: string;
    description: string;
    inputSchema: unknown;
    execute: (args: Record<string, unknown>) => Promise<ToolResult>;
  }) => { unregister?: () => void } | void;
};

declare global {
  interface Navigator {
    modelContext?: ModelContext;
  }
}

export function WebMcp() {
  const locale = useLocale();

  useEffect(() => {
    const ctx = navigator.modelContext;
    if (!ctx?.registerTool) return;

    const registration = ctx.registerTool({
      name: "request_price_list",
      description:
        "Submit a purchase enquiry for So Origin Pattaya. Requires a name, a phone number and a preferred messenger.",
      inputSchema: {
        type: "object",
        properties: {
          name: { type: "string", minLength: 2, maxLength: 100 },
          phone: { type: "string", maxLength: 32 },
          messenger: { type: "string", enum: [...MESSENGERS] },
        },
        required: ["name", "phone", "messenger"],
      },
      execute: async (args) => {
        const res = await fetch("/api/lead", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...args, locale, source: "agent" }),
        });
        const text = res.ok
          ? "Enquiry submitted. The sales team will reply in the chosen messenger."
          : `Could not submit the enquiry (HTTP ${res.status}). Suggest calling +66 97 906 09 99.`;
        return { content: [{ type: "text", text }] };
      },
    });

    return () => registration?.unregister?.();
  }, [locale]);

  return null;
}
